import { Controller } from 'react-hook-form';
import { useMemo } from 'react';
import {
  DateInput,
  IconInfo,
  formatDate,
  AccountingJobStateEnum,
  AccountingJobTypeEnum,
} from '@ui/src';
import { AccountingJobItemTypes, AccountingJobRowTypes } from './types';
import getProgress from '../../hoc/accounting-managment/helpers/get-progress';

import cn from './styles.module.css';

const AccountingJobRow = ({
  title,
  value,
  className,
}: AccountingJobRowTypes) => (
  <div className={cn.accountingJobRow}>
    <div className={cn.accountingJobRowTitle}>{title}</div>
    <div className={`${cn.accountingJobRowValue} ${className}`}>{value}</div>
  </div>
);

const AccountingJobItem = ({
  control,
  errors,
  dateWarningIsVisible,
  isEditable,
  accountingJob,
}: AccountingJobItemTypes) => {
  const updatedData = useMemo(() => {
    let type = '';
    let fullCurrentDate: string | React.ReactElement = '';
    let progressValue: string | React.ReactElement = '-';
    let startFinish = '-';

    if (accountingJob) {
      const {
        progress,
        balanceLogsProgress,
        balanceSheetProgress,
        revaluationProgress,
      } = getProgress(accountingJob);

      if (progress > 0) {
        progressValue = (
          <div
            className={`${cn.progressWrapper} ${
              progress === 100 ? cn.greenColor : ''
            } ${
              accountingJob?.state === AccountingJobStateEnum.FAILED
                ? cn.redColor
                : ''
            }`}
          >
            {accountingJob?.type === AccountingJobTypeEnum.BALANCES_LOGS &&
              (balanceLogsProgress?.toString() ||
                balanceSheetProgress?.toString() ||
                revaluationProgress?.toString()) && (
                <div className={cn.jobProgressTooltip}>
                  {`Closing Balances: ${balanceLogsProgress}%`}
                  <br />
                  {`Reports: ${balanceSheetProgress}%`}
                  <br />
                  {`Revaluation: ${revaluationProgress}%`}
                </div>
              )}
            <div className={cn.progressValue}>{`${progress}%`}</div>
            <div className={cn.progressLineWrapper}>
              <div className={cn.progressLine}>
                <div
                  className={cn.progressBar}
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </div>
        );
      }

      if (
        accountingJob?.startedAt &&
        (accountingJob?.cancelledAt || accountingJob?.finishedAt)
      ) {
        startFinish = `${formatDate(accountingJob?.startedAt, true)} - `;

        if (accountingJob?.cancelledAt) {
          startFinish = `${startFinish}${formatDate(
            accountingJob?.cancelledAt,
            true,
          )}`;
        }

        if (accountingJob?.finishedAt) {
          startFinish = `${startFinish}${formatDate(
            accountingJob?.finishedAt,
            true,
          )}`;
        }
      }

      if (accountingJob?.type === AccountingJobTypeEnum.BALANCES_LOGS) {
        type = 'Closing balances + Reports + Revaluations';
        fullCurrentDate = (
          <>
            <div className={cn.accountingJobDate}>
              {accountingJob.balancesLogsCurrentDate ? (
                <>
                  {formatDate(
                    accountingJob?.balancesLogsCurrentDate.split('T')[0],
                  )}
                  <div>Closing balances</div>
                </>
              ) : (
                ''
              )}
            </div>
            <div className={cn.accountingJobDate}>
              {accountingJob.balanceSheetCurrentDate ? (
                <>
                  {formatDate(
                    accountingJob?.balanceSheetCurrentDate.split('T')[0],
                  )}
                  <div>Reports</div>
                </>
              ) : (
                ''
              )}
            </div>
            <div className={cn.accountingJobDate}>
              {accountingJob.revaluationCurrentDate ? (
                <>
                  {formatDate(
                    accountingJob?.revaluationCurrentDate.split('T')[0],
                  )}
                  <div>Revaluations</div>
                </>
              ) : (
                ''
              )}
            </div>
          </>
        );

        if (
          !accountingJob?.balanceSheetCurrentDate &&
          !accountingJob?.balancesLogsCurrentDate &&
          !accountingJob?.revaluationCurrentDate
        ) {
          fullCurrentDate = '-';
        }
      } else if (accountingJob?.type === AccountingJobTypeEnum.BALANCE_SHEET) {
        type = 'Reports';
        fullCurrentDate = accountingJob?.balanceSheetCurrentDate
          ? formatDate(accountingJob.balanceSheetCurrentDate.split('T')[0])
          : '-';
      } else if (
        accountingJob?.type === AccountingJobTypeEnum.POST_PROCESSING
      ) {
        type =
          'Recalculation of transaction amounts in the base currency + Creation of missing statement items';
        fullCurrentDate = accountingJob?.postProcessingCurrentDate
          ? formatDate(accountingJob.postProcessingCurrentDate.split('T')[0])
          : '-';
      } else if (accountingJob?.type === AccountingJobTypeEnum.REVALUATION) {
        type = 'Revaluations';
        fullCurrentDate = accountingJob?.revaluationCurrentDate
          ? formatDate(accountingJob.revaluationCurrentDate.split('T')[0])
          : '-';
      }
    }

    return {
      type,
      fullCurrentDate,
      progressValue,
      startFinish,
    };
  }, [accountingJob]);

  return (
    <div className={cn.accountingJobItem}>
      <div className={cn.accountingJobItemTitle}>Period</div>
      <div className={cn.accountingJobPeriods}>
        <div className={cn.formGroup}>
          <Controller
            control={control}
            name="periodFrom"
            defaultValue=""
            render={({ field: { value, onChange } }) => {
              const dateValue = value?.split('T')[0];

              return (
                <DateInput
                  title="Date From"
                  value={dateValue}
                  placeholder="Select date"
                  onChange={(date) => onChange(new Date(date).toISOString())}
                  className={`${cn.periodDatePicker} ${
                    errors?.periodFrom?.message ? cn.withError : ''
                  }`}
                  error={errors?.periodFrom?.message}
                  disabledAfter={
                    accountingJob?.type ===
                    AccountingJobTypeEnum.POST_PROCESSING
                      ? new Date()
                      : new Date(new Date().setDate(new Date().getDate() - 1))
                  }
                  disabled={!isEditable}
                  withoutCustomSelector
                  fullWidth
                />
              );
            }}
          />
        </div>
        <div className={`${cn.formGroup} ${cn.withFlex}`}>
          <DateInput
            title="Date To"
            value={
              accountingJob?.state !== AccountingJobStateEnum.NEW
                ? accountingJob?.periodTo?.split('T')[0]
                : ''
            }
            placeholder="-"
            className={cn.periodDatePicker}
            disabled
            withoutCustomSelector
            fullWidth
          />
          <div className={cn.periodToHintWrapper}>
            <IconInfo />
            <div className={cn.periodToHint}>
              The date is set to the current date when the task is started.
            </div>
          </div>
        </div>
        {dateWarningIsVisible && (
          <div className={cn.dateWarning}>
            <div className={cn.dateWarningLeft}>
              <IconInfo width={16} height={16} />
              <div className={cn.dateWarningText}>
                Possibly incorrect <span>«Date to»&nbsp;</span> specified,
                please choose up to&nbsp;
                <span>
                  {accountingJob?.type === AccountingJobTypeEnum.POST_PROCESSING
                    ? new Date().toLocaleDateString()
                    : new Date(
                        new Date().setDate(new Date().getDate() - 1),
                      ).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className={cn.accountingJobItemTitle}>Details</div>
      <AccountingJobRow title="Type" value={updatedData.type} />
      <AccountingJobRow
        title="Status"
        value={
          accountingJob?.state
            ?.split('_')
            ?.map((item) => `${item.charAt(0)}${item.slice(1).toLowerCase()}`)
            ?.join(' ') as string
        }
        className={
          accountingJob?.state === AccountingJobStateEnum.FAILED
            ? cn.jobStateFailed
            : ''
        }
      />
      <AccountingJobRow
        title="Created"
        value={
          accountingJob?.createdAt
            ? formatDate(accountingJob?.createdAt, true)
            : '-'
        }
      />
      <AccountingJobRow
        title="Accounts"
        value={
          accountingJob?.accountsIds && accountingJob?.accountsIds?.length
            ? accountingJob?.accountsIds?.length?.toString()
            : 'All'
        }
      />
      <AccountingJobRow
        title="Processing Date"
        value={updatedData.fullCurrentDate}
      />
      <AccountingJobRow title="Progress" value={updatedData.progressValue} />
      <AccountingJobRow
        title="Started-Finished"
        value={updatedData.startFinish}
      />
      <AccountingJobRow
        title="Duration"
        value={accountingJob?.duration || '-'}
      />
    </div>
  );
};

export default AccountingJobItem;
